import React, { useRef, useState } from "react";
import { Button, Row, Col, Label } from "reactstrap";
import CommonFileProgress from "Components/Common/CommonFileUpload/CommonFileProgress";
import TruncatedText from "Components/Common/TruncatedText";
import addVolunteerStyle from "../../../Volunteer/MasterVolunteer/css/volunteer.module.scss";
import ipStyle from "../css/ipInventory.module.scss";
import { showToastMessage } from "../../../../common/service/CommonService";
import { parseSizeToBytes } from "../helpers/ipInventory.helper";
import { IP_ATTACHMENT_OPTIONS } from "../helpers/ipInventory.constant";

interface AttachmentManagementSectionProps {
  uploadedFiles: File[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  existingAttachments: any[];
  setExistingAttachments: React.Dispatch<React.SetStateAction<any[]>>;
  deletedAttachmentIds: number[];
  setDeletedAttachmentIds: React.Dispatch<React.SetStateAction<number[]>>;
  selectedStudyCode?: string;
  handlePreview: (file: any) => void;
  coaError?: string | null;
}

const AttachmentManagementSection: React.FC<AttachmentManagementSectionProps> = ({
  uploadedFiles,
  setUploadedFiles,
  existingAttachments,
  setExistingAttachments,
  setDeletedAttachmentIds,
  selectedStudyCode,
  handlePreview,
  coaError,
}) => {
  const [activeAttachmentType, setActiveAttachmentType] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB limit

  const handleMultipleFilesAdd = (files: File[]) => {
    if (!activeAttachmentType) {
      showToastMessage("error", "Please select an attachment type first.");
      return;
    }

    if (files.length === 0) return;

    const currentTotal = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
    const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + incomingTotal > MAX_TOTAL_SIZE) {
      showToastMessage("error", "Total attachment size cannot exceed 20 MB.");
      return;
    }

    const taggedFiles = files.map((file) => {
      Object.defineProperty(file, "attachmenttype", {
        value: activeAttachmentType,
        writable: true,
        enumerable: true,
        configurable: true,
      });
      return file;
    });

    setUploadedFiles((prev) => [...prev, ...taggedFiles]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleMultipleFilesAdd(files);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleMultipleFilesAdd(files);
  };

  const removeFile = (index: number) =>
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

  const handleDeleteExisting = (attachmentId: number) => {
    setDeletedAttachmentIds((prev) => [...prev, attachmentId]);
    setExistingAttachments((prev) =>
      prev.filter((a) => Number(a.inventoryattachmentid) !== Number(attachmentId))
    );
  };

  const existingTotalBytes = existingAttachments.reduce(
    (sum, f) => sum + parseSizeToBytes(f.filesize || f.size),
    0
  );

  const getTypeLabel = (val: string) => {
    const matched = IP_ATTACHMENT_OPTIONS.find((o) => o.value === val);
    return matched ? matched.label : val || "Document";
  };

  return (
    <div className="mb-4">
      {/* 1. Attachment Type Chips */}
      <div className="mb-4">
        <Label className="fw-bold text-dark mb-2 small">
          Select Attachment Type
        </Label>
        <div className="d-flex flex-wrap gap-2">
          {IP_ATTACHMENT_OPTIONS.map((opt: any) => {
            const isUploaded =
              existingAttachments.some(
                (f) => String(f.attachmenttype || f.filetype).toLowerCase() === String(opt.value).toLowerCase()
              ) ||
              uploadedFiles.some(
                (f: any) => String(f.attachmenttype).toLowerCase() === String(opt.value).toLowerCase()
              );
            const isActive = activeAttachmentType === opt.value;

            const chipClass = [
              ipStyle.attachmentChip,
              isActive ? ipStyle.isActive : isUploaded ? ipStyle.isUploaded : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <div
                key={opt.value}
                className={chipClass}
                onClick={() => {
                  if (isActive) {
                    setActiveAttachmentType(null);
                  } else {
                    setActiveAttachmentType(opt.value);
                  }
                }}
              >
                <i
                  className={`${isActive
                      ? "ri-checkbox-circle-fill text-primary"
                      : isUploaded
                        ? "ri-check-line text-success"
                        : "ri-add-circle-line"
                    } me-2`}
                  style={{ fontSize: "16px" }}
                />
                {opt.label}
              </div>
            );
          })}
        </div>
        {coaError && (
          <div className="text-danger small mt-2 fw-semibold d-flex align-items-center">
            <i className="ri-error-warning-fill me-1 fs-14 text-danger" />
            {coaError}
          </div>
        )}
      </div>

      {/* 2. File Upload Dropzone */}
      <Label className="fw-bold text-dark mb-2 small">Upload Documents</Label>
      <CommonFileProgress
        files={uploadedFiles}
        existingSizeBytes={existingTotalBytes}
        maxSizeMB={20}
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (activeAttachmentType !== null) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => {
          if (activeAttachmentType === null) return;
          fileInputRef.current?.click();
        }}
        style={{
          border: `1px dashed ${isDragging ? "#2E86AB" : "#d0d5dd"}`,
          borderRadius: 8,
          padding: "30px 20px",
          textAlign: "center",
          cursor: activeAttachmentType === null ? "not-allowed" : "pointer",
          background: activeAttachmentType === null ? "#f8fafc" : isDragging ? "#f0f7ff" : "#fafbfc",
          opacity: activeAttachmentType === null ? 0.7 : 1,
          transition: "all 0.2s",
        }}
      >
        <i
          className="ri-upload-cloud-2-line text-primary"
          style={{ fontSize: 32, display: "block", marginBottom: 8 }}
        />
        <p className="mb-1 fw-bold text-dark" style={{ fontSize: 14 }}>
          {activeAttachmentType === null
            ? "Select an attachment type above to enable upload"
            : `Click to upload or drag & drop documents for ${getTypeLabel(activeAttachmentType)}`}
        </p>
        <p className="text-muted mb-3" style={{ fontSize: 12 }}>
          Supports PDF, JPG, PNG (Select multiple files, Max 20MB total)
        </p>
        <Button
          type="button"
          color="light"
          size="sm"
          className="border px-3"
          onClick={(e) => {
            e.stopPropagation();
            if (activeAttachmentType === null) return;
            fileInputRef.current?.click();
          }}
          disabled={!selectedStudyCode || activeAttachmentType === null}
        >
          Browse Files
        </Button>
        {/* Hidden file input supporting multiple files per type */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      {/* Existing attachments from server */}
      {existingAttachments.length > 0 && (
        <div className="mt-3 d-flex flex-column gap-2">
          <p className="text-muted small fw-medium mb-1">Existing Attachments</p>
          <Row className="gap-2 px-0 w-100 mx-auto">
            {existingAttachments.map((file, idx) => (
              <Col
                key={`existing-${idx}`}
                md={4}
                className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
              >
                <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                  <div className={addVolunteerStyle.fileInfo}>
                    <div className={addVolunteerStyle.fileIcon}>
                      <i className="ri-file-pdf-line text-danger" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={addVolunteerStyle.fileName}>
                        <TruncatedText text={file.filename} />
                      </div>
                      <div className={addVolunteerStyle.fileMeta}>
                        <span className="text-uppercase me-2 fw-semibold">
                          {getTypeLabel(file.attachmenttype || file.filetype)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={addVolunteerStyle.fileActions}>
                    <i
                      className="ri-eye-line text-primary"
                      title="Preview"
                      style={{ cursor: "pointer", marginRight: "10px" }}
                      onClick={() => handlePreview(file)}
                    />
                    <i
                      className="ri-delete-bin-line text-danger"
                      title="Delete"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleDeleteExisting(file.inventoryattachmentid)}
                    />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* Newly uploaded files */}
      {uploadedFiles.length > 0 && (
        <div className="mt-3 d-flex flex-column gap-2">
          <p className="text-muted small fw-medium mb-1">New Attachments</p>
          <Row className="gap-2 px-0 w-100 mx-auto">
            {uploadedFiles.map((file: any, idx) => (
              <Col
                key={idx}
                md={4}
                sm={12}
                className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
              >
                <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                  <div className={addVolunteerStyle.fileInfo}>
                    <div className={addVolunteerStyle.fileIcon}>
                      <i className="ri-file-pdf-line text-danger" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={addVolunteerStyle.fileName}>
                        <TruncatedText text={file.name} />
                      </div>
                      <div className={addVolunteerStyle.fileMeta}>
                        <span className="badge bg-light text-primary me-2 border">
                          {getTypeLabel(file.attachmenttype)}
                        </span>
                        {(file.size / 1024).toFixed(2)} KB
                      </div>
                    </div>
                  </div>
                  <div className={addVolunteerStyle.fileActions}>
                    <i
                      className="ri-eye-line text-primary"
                      title="Preview"
                      style={{ cursor: "pointer", marginRight: "10px" }}
                      onClick={() => handlePreview(file)}
                    />
                    <i
                      className="ri-delete-bin-line text-danger"
                      title="Delete"
                      style={{ cursor: "pointer" }}
                      onClick={() => removeFile(idx)}
                    />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}
    </div>
  );
};

export default AttachmentManagementSection;
